import { Controller } from "@hotwired/stimulus"

import { writePagePath } from "../lib/page_path"

// The filters above a creator's map and list (creators/pages/_place_filters):
// one place on the left (a city or a neighbourhood, or "All"), any number of
// types on the right. Rows carry data-filter-where and data-filter-kind; the
// ones left visible go out in a `change` event, which the map takes to hide
// its other pins. Nothing is fetched: the page already holds every place.
export default class extends Controller {
  static targets = ["where", "kind", "typeButton", "typeLabel", "menu", "empty"]

  connect() {
    this.where = ""
    this.closeOnOutside = (event) => {
      if (this.hasMenuTarget && !this.menuTarget.parentElement.contains(event.target)) this.closeMenu()
    }
    this.closeOnEscape = (event) => { if (event.key === "Escape") this.closeMenu() }
    document.addEventListener("click", this.closeOnOutside)
    document.addEventListener("keydown", this.closeOnEscape)
    this.fromUrl()
  }

  // A shared address opens on its filters (28 Oct 2026): the server marked
  // the chip and ticked the boxes; they are applied a tick later, once the
  // map and the posts' controllers listen.
  fromUrl() {
    const chip = this.whereTargets.find((c) => c.dataset.on === "true" && c.dataset.value)
    if (chip) this.where = chip.dataset.value
    const ticked = this.kindTargets.some((box) => box.checked)
    if (ticked) this.labelKinds()
    if (chip || ticked) setTimeout(() => this.apply({ write: false }), 0)
  }

  // The filters into the address (lib/page_path), the tab kept.
  toUrl() {
    const chip = this.whereTargets.find((c) => c.dataset.value === this.where)
    this.element.dataset.urlWhere = this.where ? chip?.dataset.slug || "" : ""
    this.element.dataset.urlTypes = this.kindTargets.filter((box) => box.checked).map((box) => box.value).join("+")
    writePagePath(this.element)
  }

  pickWhere(event) {
    this.where = event.currentTarget.dataset.value
    this.whereTargets.forEach((chip) => { chip.dataset.on = chip === event.currentTarget })
    this.apply()
  }

  pickKinds() {
    this.labelKinds()
    this.apply()
  }

  labelKinds() {
    const picked = this.kindTargets.filter((box) => box.checked)
    // Two names fit the button; past that, the first and a count of the rest.
    const names = picked.map((box) => box.dataset.label)
    this.typeLabelTarget.textContent = names.length === 0 ? "All types"
      : names.length <= 2 ? names.join(", ") : `${names[0]} +${names.length - 1}`
    this.typeButtonTarget.dataset.on = picked.length > 0
  }

  toggleMenu() {
    const open = this.menuTarget.classList.toggle("hidden") === false
    this.typeButtonTarget.setAttribute("aria-expanded", open)
  }

  closeMenu() {
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.add("hidden")
    this.typeButtonTarget.setAttribute("aria-expanded", false)
  }

  apply({ write = true } = {}) {
    if (write) this.toUrl()
    const kinds = new Set(this.kindTargets.filter((box) => box.checked).map((box) => box.value))
    const ids = []
    this.element.querySelectorAll("[data-row][data-filter-kind]").forEach((row) => {
      const shown = (this.where === "" || row.dataset.filterWhere === this.where)
        && (kinds.size === 0 || kinds.has(row.dataset.filterKind))
      row.classList.toggle("hidden", !shown)
      if (shown) ids.push(row.dataset.row)
    })
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", ids.length > 0)
    // The Places tab's count follows (28 Oct 2026).
    const count = this.element.querySelector("[data-tab-count=places]")
    if (count) count.textContent = ids.length
    this.dispatch("change", { detail: { ids } })
  }
}
